import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { 
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, DollarSign, Package, Shield, MapPin, 
  Calendar, AlertTriangle, ArrowLeft 
} from 'lucide-react';
import { assetService } from '../../services/assetService';
import { userProfileService } from '../../services/userProfileService';
import { formatCurrency } from '../../utils/currencyFormatter';

export default function AssetManagementAnalytics() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userCurrency, setUserCurrency] = useState('USD');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assetsData, statsData, profile] = await Promise.all([
        assetService?.getAll(),
        assetService?.getStatistics(),
        userProfileService?.getCurrentProfile()
      ]);
      setAssets(assetsData);
      setStatistics(statsData);
      setUserCurrency(profile?.currency || 'USD');
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  // Prepare data for charts
  const getAssetTypeDistribution = () => {
    const typeCounts = {};
    assets?.forEach(asset => {
      typeCounts[asset?.assetType] = (typeCounts?.[asset?.assetType] || 0) + 1;
    });
    return Object.entries(typeCounts)?.map(([name, value]) => ({ name, value }));
  };

  const getAssetValueByType = () => {
    const typeValues = {};
    assets?.forEach(asset => {
      const type = asset?.assetType;
      typeValues[type] = (typeValues?.[type] || 0) + parseFloat(asset?.assetValue || 0);
    });
    return Object.entries(typeValues)?.map(([type, value]) => ({ 
      type, 
      value: parseFloat(value?.toFixed(2)) 
    }));
  };

  const getLocationDistribution = () => {
    return [
      { name: 'In Office', value: statistics?.inOfficeCount || 0 },
      { name: 'Remote', value: statistics?.remoteCount || 0 }
    ];
  };

  const getWarrantyExpiryTimeline = () => {
    const now = new Date();
    const upcoming = assets?.filter(asset => {
      if (!asset?.warrantyExpiry) return false;
      const expiryDate = new Date(asset?.warrantyExpiry);
      const daysUntilExpiry = Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry > 0 && daysUntilExpiry <= 180; // Next 6 months
    })?.sort((a, b) => new Date(a?.warrantyExpiry) - new Date(b?.warrantyExpiry));

    return upcoming?.map(asset => ({
      name: asset?.assetName?.substring(0, 20),
      daysUntilExpiry: Math.floor((new Date(asset?.warrantyExpiry) - now) / (1000 * 60 * 60 * 24)),
      value: parseFloat(asset?.assetValue || 0)
    }));
  };

  const getInsuranceStatus = () => {
    return [
      { name: 'Insured', value: statistics?.insuredCount || 0 },
      { name: 'Uninsured', value: (statistics?.totalAssets || 0) - (statistics?.insuredCount || 0) }
    ];
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col ml-64">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading analytics...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col ml-64">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
                <p className="text-red-800 font-medium">Error loading analytics</p>
                <p className="text-red-600 text-sm mt-2">{error}</p>
                <button
                  onClick={loadData}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-64">
        <Header />
        
        <main className="flex-1 overflow-auto p-8">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/asset-management-overview')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Overview
              </button>
              <h1 className="text-3xl font-bold text-gray-900">Asset Analytics & Statistics</h1>
              <p className="text-gray-600 mt-1">Comprehensive visual insights into your asset portfolio</p>
            </div>
          </div>

          {/* Key Metrics Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Total Assets</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{statistics?.totalAssets || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Across all categories</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Total Value</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatCurrency(statistics?.totalValue || 0, userCurrency)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Current portfolio value</p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Insurance Coverage</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {statistics?.insuredCount || 0}/{statistics?.totalAssets || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {((statistics?.insuredCount || 0) / (statistics?.totalAssets || 1) * 100)?.toFixed(0)}% insured
                  </p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Average Value</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatCurrency(
                      (statistics?.totalValue || 0) / (statistics?.totalAssets || 1), 
                      userCurrency
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Per asset</p>
                </div>
                <div className="bg-orange-100 p-3 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Asset Distribution by Type */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Asset Distribution by Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getAssetTypeDistribution()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getAssetTypeDistribution()?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Asset Value by Type */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Asset Value by Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getAssetValueByType()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value, userCurrency)}
                  />
                  <Legend />
                  <Bar dataKey="value" fill="#3B82F6" name="Total Value" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Location Distribution */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gray-600" />
                Location Distribution
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getLocationDistribution()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#F59E0B" />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Insurance Status */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-gray-600" />
                Insurance Coverage Status
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getInsuranceStatus()} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#8B5CF6" name="Count" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Warranty Expiry Timeline */}
          {getWarrantyExpiryTimeline()?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-600" />
                Upcoming Warranty Expirations (Next 6 Months)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={getWarrantyExpiryTimeline()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'daysUntilExpiry') return `${value} days`;
                      if (name === 'value') return formatCurrency(value, userCurrency);
                      return value;
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="daysUntilExpiry" 
                    stroke="#EF4444" 
                    name="Days Until Expiry"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {getWarrantyExpiryTimeline()?.length} asset(s) have warranties expiring in the next 6 months
                </p>
              </div>
            </div>
          )}

          {/* Additional Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h4 className="text-sm font-medium text-gray-600 mb-4">Asset Categories Breakdown</h4>
              <div className="space-y-3">
                {getAssetTypeDistribution()?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">{item?.name}</span>
                    <span className="text-sm font-semibold text-gray-900">{item?.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h4 className="text-sm font-medium text-gray-600 mb-4">Value Distribution</h4>
              <div className="space-y-3">
                {getAssetValueByType()?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">{item?.type}</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(item?.value, userCurrency)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h4 className="text-sm font-medium text-gray-600 mb-4">Key Insights</h4>
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-800">
                    Most common asset type: {getAssetTypeDistribution()?.[0]?.name}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-800">
                    {((statistics?.insuredCount || 0) / (statistics?.totalAssets || 1) * 100)?.toFixed(0)}% of assets are insured
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-800">
                    {statistics?.inOfficeCount || 0} assets in office, {statistics?.remoteCount || 0} remote
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}